import { commaListSchema } from "@birdwatch/common";
import { z } from "zod";
import {
  type FilterUrlKeys,
  withOperators,
} from "@/features/shared/filters-new";

/**
 * The Lists tab's own keys (kebab-case, like the place and order routes' other keys): its view and
 * done-or-archived toggle, and the shared filter bar's keys under names of the tab's own, so its
 * filters never mix with another list's on the same page (the order page's Files tabs). Values are
 * the API's.
 */
export const LIST_ITEMS_URL_KEYS = {
  /** "list"; absent is the board. */
  view: "list-items-view",
  /** "1" shows done and archived items; absent hides them. */
  closed: "list-items-closed",
  /** The Type and Proposed by filters: `list-items-filters[workType]=FIX,SERVICE`. */
  filters: "list-items-filters",
  /** The filter chips' order, and a chip added before its value is picked. */
  filtersOrder: "list-items-filters-order",
  // The tab has no search box and no sort. The shared keys name both, and nothing writes them, so
  // the routes do not declare them.
  search: "list-items-search",
  sort: "list-items-sort",
} as const satisfies FilterUrlKeys & { view: string; closed: string };

/**
 * The Lists tab's URL keys, for the `validateSearch` of every route that shows the tab: the
 * place detail, and the order detail's side panel. Lenient on purpose: a strict enum throws
 * `SearchParamError` on an old link and takes the page down (webapp-routing.md); the tab
 * narrows the values. A filter missing here is stripped from the URL, so its chip never shows:
 * every field of `listItemsFilterFields` needs its key.
 */
export const listItemsSearchFragment = {
  [LIST_ITEMS_URL_KEYS.view]: z.string().optional(),
  [LIST_ITEMS_URL_KEYS.closed]: z.string().optional(),
  [LIST_ITEMS_URL_KEYS.filters]: z
    .object({
      workType: withOperators(commaListSchema),
      proposedBy: withOperators(commaListSchema),
    })
    .optional(),
  [LIST_ITEMS_URL_KEYS.filtersOrder]: z.string().optional(),
};
